import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { SiteSummary } from '../../models/cms-models';
import { SiteService } from '../../services/site-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-sites-page',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './sites-page.html',
  styleUrl: './sites-page.css'
})
export class SitesPage implements OnInit {
  private siteService = inject(SiteService);
  private siteContext = inject(SiteContextService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  activeSiteId: string | null = null;
  draftName = '';
  error: string | null = null;
  showCreateSiteForm = false;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    this.siteContext.sites$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sites) => {
      this.sites = sites;
    });
    this.siteContext.activeSiteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeSiteId) => {
      this.activeSiteId = activeSiteId;
    });
  }

  async createSite(): Promise<void> {
    if (!this.draftName.trim()) {
      return;
    }

    try {
      await this.siteContext.ensureLoaded();
      const created = await this.siteService.createSite(this.draftName.trim());
      this.siteContext.upsertSite(created, { setActive: true });
      this.draftName = '';
      this.showCreateSiteForm = false;
      this.error = null;
      await this.router.navigate(['/sites', created.id]);
    } catch {
      this.error = 'Failed to create site.';
    }
  }

  openCreateSiteForm(): void {
    this.showCreateSiteForm = true;
  }

  closeCreateSiteForm(): void {
    this.showCreateSiteForm = false;
    this.draftName = '';
  }

  async openDashboard(siteId: string): Promise<void> {
    this.siteContext.setActiveSite(siteId);
    await this.router.navigate(['/']);
  }

  async openLandingPages(siteId: string): Promise<void> {
    this.siteContext.setActiveSite(siteId);
    await this.router.navigate(['/landing-pages']);
  }

  async openLinks(siteId: string): Promise<void> {
    this.siteContext.setActiveSite(siteId);
    await this.router.navigate(['/links']);
  }

  get totalLandingPages(): number {
    return this.sites.reduce((sum, site) => sum + site.landingPageCount, 0);
  }

  get totalApiKeys(): number {
    return this.sites.reduce((sum, site) => sum + site.publicApiKeyCount, 0);
  }

  get totalLinks(): number {
    return this.sites.reduce((sum, site) => sum + site.linkCount, 0);
  }

  get activeSiteName(): string | null {
    return this.sites.find((site) => site.id === this.activeSiteId)?.name ?? null;
  }
}
